import React from 'react';
import {
  TrendingUp, TrendingDown, Award, Target, Info, Plus, Trash2,
  LineChart, Mail, Bell, Flag
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Progress } from '../ui/progress';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

export function BenchmarkTrendsTab({
  goals,
  goalsProgress,
  trendsData,
  trendsDays,
  setTrendsDays,
  showMultiLine,
  setShowMultiLine,
  setGoalDialogOpen,
  deleteGoal,
  notificationHistory
}) {
  return (
    <div className="space-y-6">
      {/* Goals Section */}
      <Card className="border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50" data-testid="goals-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-base">
            <div className="flex items-center gap-2">
              <Flag className="h-5 w-5 text-amber-600" />
              Benchmark Goals
              {goals.length > 0 && (
                <Badge className="bg-amber-100 text-amber-700">{goals.length}</Badge>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setGoalDialogOpen(true)}
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
              data-testid="add-goal-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Goal
            </Button>
          </CardTitle>
          <CardDescription>Set target percentile ranks to track your improvement</CardDescription>
        </CardHeader>
        <CardContent>
          {goalsProgress?.has_goals && goalsProgress?.has_data ? (
            <div className="space-y-3">
              {goalsProgress.progress.map((p) => (
                <div
                  key={p.goal_id}
                  className={`p-3 rounded-lg border ${
                    p.status === 'achieved' ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'
                  }`}
                  data-testid={`goal-progress-${p.goal_id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Target className={`h-4 w-4 ${p.status === 'achieved' ? 'text-emerald-600' : 'text-slate-400'}`} />
                      <span className="font-medium text-sm">{p.metric_name}</span>
                      {p.status === 'achieved' && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">Achieved!</Badge>
                      )}
                      {goals.find(g => g.id === p.goal_id)?.notification_settings?.email_enabled && (
                        <Bell className="h-3 w-3 text-amber-500" title="Email notifications enabled" />
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteGoal(p.goal_id)}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-rose-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-slate-500">
                      Current: <strong className="text-slate-800">{p.current_percentile}th</strong>
                    </span>
                    <span className="text-slate-500">
                      Target: <strong className="text-amber-700">{p.target_percentile}th</strong>
                    </span>
                    <span className={`${p.gap > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {p.gap > 0 ? `${p.gap} to go` : 'Target reached!'}
                    </span>
                  </div>
                  <Progress 
                    value={p.percent_complete} 
                    className={`mt-2 h-2 ${p.status === 'achieved' ? 'bg-emerald-100' : 'bg-slate-100'}`}
                  />
                </div>
              ))}
            </div>
          ) : goals.length > 0 ? (
            <div className="text-center py-4">
              <Info className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">View your benchmark position to track goal progress</p>
            </div>
          ) : (
            <div className="text-center py-4">
              <Target className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No goals set yet. Add a goal to track your improvement!</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Notification History */}
      {notificationHistory.length > 0 && (
        <Card className="border border-slate-200" data-testid="notification-history-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="h-5 w-5 text-slate-500" />
              Recent Notifications
              <Badge variant="outline" className="text-xs">{notificationHistory.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {notificationHistory.slice(0, 5).map((notif) => (
                <div
                  key={notif.id}
                  className={`p-3 rounded-lg border ${
                    notif.type === 'goal_achieved' ? 'bg-emerald-50 border-emerald-200' :
                    notif.type === 'rank_drop' ? 'bg-rose-50 border-rose-200' :
                    'bg-blue-50 border-blue-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {notif.type === 'goal_achieved' && <Award className="h-4 w-4 text-emerald-600" />}
                    {notif.type === 'milestone' && <TrendingUp className="h-4 w-4 text-blue-600" />}
                    {notif.type === 'rank_drop' && <TrendingDown className="h-4 w-4 text-rose-600" />}
                    <span className="font-medium text-sm">
                      {notif.type === 'goal_achieved' && 'Goal Achieved!'}
                      {notif.type === 'milestone' && `Milestone: ${notif.extra_data?.milestone}%`}
                      {notif.type === 'rank_drop' && `Rank Dropped: -${notif.extra_data?.drop_amount?.toFixed(1)}`}
                    </span>
                    <span className="text-xs text-slate-500 ml-auto">
                      {new Date(notif.sent_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    {notif.metric_name}: {notif.current_percentile}th percentile
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Trends Chart */}
      <Card data-testid="trends-chart-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <LineChart className="h-5 w-5 text-indigo-600" />
              Percentile Trends
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Label htmlFor="multi-line" className="text-sm text-slate-600">
                  Show all metrics
                </Label>
                <Switch
                  id="multi-line"
                  checked={showMultiLine}
                  onCheckedChange={setShowMultiLine}
                />
              </div>
              <Select value={trendsDays.toString()} onValueChange={(v) => setTrendsDays(parseInt(v))}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="180">6 months</SelectItem>
                  <SelectItem value="365">1 year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardTitle>
          <CardDescription>Track your benchmark position over time</CardDescription>
        </CardHeader>
        <CardContent>
          {trendsData?.has_data ? (
            <div className="space-y-6">
              {/* Summary Stats */}
              {trendsData.summary && trendsData.summary.change !== undefined && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Starting</p>
                    <p className="text-lg font-bold text-slate-700">{trendsData.summary.starting_percentile}th</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Current</p>
                    <p className="text-lg font-bold text-indigo-700">{trendsData.summary.current_percentile}th</p>
                  </div>
                  <div className={`text-center p-3 rounded-lg ${
                    trendsData.summary.trend_direction === 'improving' ? 'bg-emerald-50' :
                    trendsData.summary.trend_direction === 'declining' ? 'bg-rose-50' : 'bg-slate-50'
                  }`}>
                    <p className="text-xs text-slate-500">Change</p>
                    <p className={`text-lg font-bold ${
                      trendsData.summary.change > 0 ? 'text-emerald-600' :
                      trendsData.summary.change < 0 ? 'text-rose-600' : 'text-slate-600'
                    }`}>
                      {trendsData.summary.change > 0 ? '+' : ''}{trendsData.summary.change}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Data Points</p>
                    <p className="text-lg font-bold text-slate-700">{trendsData.summary.snapshots_count}</p>
                  </div>
                </div>
              )}
              
              {/* Chart */}
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsLineChart
                    data={showMultiLine ? null : trendsData.overall_trend.map(t => ({
                      ...t,
                      date: new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    }))}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                      labelStyle={{ fontWeight: 'bold' }}
                    />
                    <Legend />
                    
                    {/* Goal reference lines */}
                    {goals.filter(g => !g.metric_id).map(g => (
                      <ReferenceLine 
                        key={g.id}
                        y={g.target_percentile} 
                        stroke="#f59e0b" 
                        strokeDasharray="5 5"
                        label={{ value: `Goal: ${g.target_percentile}th`, fill: '#f59e0b', fontSize: 11 }}
                      />
                    ))}
                    
                    {!showMultiLine ? (
                      <Line
                        type="monotone"
                        dataKey="percentile"
                        name="Overall Percentile"
                        stroke="#6366f1"
                        strokeWidth={2}
                        dot={{ fill: '#6366f1', strokeWidth: 2 }}
                        activeDot={{ r: 6 }}
                      />
                    ) : (
                      trendsData.metric_trends?.map((metric, idx) => {
                        const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
                        return (
                          <Line
                            key={metric.metric_id}
                            type="monotone"
                            data={metric.data.map(d => ({
                              ...d,
                              date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                            }))}
                            dataKey="percentile"
                            name={metric.metric_name}
                            stroke={colors[idx % colors.length]}
                            strokeWidth={2}
                            dot={{ fill: colors[idx % colors.length], strokeWidth: 1 }}
                          />
                        );
                      })
                    )}
                  </RechartsLineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <LineChart className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No trend data yet</p>
              <p className="text-sm text-slate-400 mt-1">
                View your benchmark position to start recording trends
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
